import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layouts/AdminLayout";
import CloudinaryUpload from "@/components/CloudinaryUpload";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  Trash2,
  Banknote,
  CreditCard,
  Shield,
  ExternalLink,
  MousePointer,
} from "lucide-react";
import { toast } from "sonner";

const AdminFinance = () => {
  const { getAuthHeaders } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  const [form, setForm] = useState({
    name: "",
    company_name: "",
    category: "personal_loan",
    description: "",
    logo_url: "",
    affiliate_link: "",
    features: "",
    commission_percent: "",
    min_amount: "",
    max_amount: "",
    interest_rate: "",
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/admin/finance/products`, { headers: getAuthHeaders() });
      setProducts(res.data.products || []);
    } catch (error) {
      toast.error("Failed to load products");
    }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({
      name: "",
      company_name: "",
      category: "personal_loan",
      description: "",
      logo_url: "",
      affiliate_link: "",
      features: "",
      commission_percent: "",
      min_amount: "",
      max_amount: "",
      interest_rate: "",
    });
    setEditingProduct(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      name: form.name,
      company_name: form.company_name,
      category: form.category,
      description: form.description,
      logo_url: form.logo_url || null,
      affiliate_link: form.affiliate_link,
      features: form.features.split("\n").filter(f => f.trim()),
      commission_percent: parseFloat(form.commission_percent),
      min_amount: form.min_amount ? parseFloat(form.min_amount) : null,
      max_amount: form.max_amount ? parseFloat(form.max_amount) : null,
      interest_rate: form.interest_rate || null,
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/finance/products/${editingProduct.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Product updated!");
      } else {
        await axios.post(`${API}/admin/finance/products`, payload, { headers: getAuthHeaders() });
        toast.success("Product created!");
      }
      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
    setSubmitting(false);
  };

  const handleEdit = (product) => {
    setForm({
      name: product.name,
      company_name: product.company_name,
      category: product.category,
      description: product.description,
      logo_url: product.logo_url || "",
      affiliate_link: product.affiliate_link,
      features: (product.features || []).join("\n"),
      commission_percent: product.commission_percent.toString(),
      min_amount: product.min_amount?.toString() || "",
      max_amount: product.max_amount?.toString() || "",
      interest_rate: product.interest_rate || "",
    });
    setEditingProduct(product);
    setDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!confirm("Delete this financial product?")) return;

    try {
      await axios.delete(`${API}/admin/finance/products/${productId}`, { headers: getAuthHeaders() });
      toast.success("Product deleted");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "credit_card": return CreditCard;
      case "insurance": return Shield;
      default: return Banknote;
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case "credit_card": return "Credit Card";
      case "insurance": return "Insurance";
      default: return "Personal Loan";
    }
  };

  // Stats
  const stats = {
    total: products.length,
    loans: products.filter(p => p.category === "personal_loan").length,
    cards: products.filter(p => p.category === "credit_card").length,
    insurance: products.filter(p => p.category === "insurance").length,
    totalClicks: products.reduce((sum, p) => sum + (p.click_count || 0), 0),
  };

  return (
    <AdminLayout title="Financial Products">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-gray-500">Manage loans, credit cards, and insurance products</p>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-finance-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>{editingProduct ? "Edit" : "Add"} Financial Product</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Product Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g., Personal Loan"
                    required
                  />
                </div>
                <div>
                  <Label>Company Name *</Label>
                  <Input
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    placeholder="e.g., HDFC Bank"
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal_loan">Personal Loan</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="insurance">Insurance</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description of the product..."
                  required
                />
              </div>

              <div>
                <Label>Logo</Label>
                <CloudinaryUpload
                  value={form.logo_url}
                  onChange={(url) => setForm({ ...form, logo_url: url })}
                  folder="finance/"
                  resourceType="image"
                  testId="finance-logo-upload"
                />
              </div>

              <div>
                <Label>Affiliate Link *</Label>
                <Input
                  value={form.affiliate_link}
                  onChange={(e) => setForm({ ...form, affiliate_link: e.target.value })}
                  placeholder="https://..."
                  required
                />
              </div>

              <div>
                <Label>Features (one per line)</Label>
                <Textarea
                  value={form.features}
                  onChange={(e) => setForm({ ...form, features: e.target.value })}
                  placeholder="Low interest rate&#10;Instant approval&#10;No documentation"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Commission % *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.commission_percent}
                    onChange={(e) => setForm({ ...form, commission_percent: e.target.value })}
                    placeholder="e.g., 2"
                    required
                  />
                </div>
                <div>
                  <Label>Interest Rate</Label>
                  <Input
                    value={form.interest_rate}
                    onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
                    placeholder="e.g., 10.5% p.a."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Min Amount (₹)</Label>
                  <Input
                    type="number"
                    value={form.min_amount}
                    onChange={(e) => setForm({ ...form, min_amount: e.target.value })}
                    placeholder="e.g., 50000"
                  />
                </div>
                <div>
                  <Label>Max Amount (₹)</Label>
                  <Input
                    type="number"
                    value={form.max_amount}
                    onChange={(e) => setForm({ ...form, max_amount: e.target.value })}
                    placeholder="e.g., 5000000"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={submitting}>
                {submitting ? "Saving..." : editingProduct ? "Update Product" : "Create Product"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-500">Total Products</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.loans}</p>
            <p className="text-sm text-gray-500">Loans</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.cards}</p>
            <p className="text-sm text-gray-500">Credit Cards</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.insurance}</p>
            <p className="text-sm text-gray-500">Insurance</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.totalClicks}</p>
            <p className="text-sm text-gray-500">Total Clicks</p>
          </CardContent>
        </Card>
      </div>

      {/* Products Table */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : products.length > 0 ? (
        <Card className="border-0 shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Details</TableHead>
                <TableHead className="text-center">Commission</TableHead>
                <TableHead className="text-center">Clicks</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => {
                const Icon = getCategoryIcon(product.category);
                return (
                  <TableRow key={product.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {product.logo_url ? (
                          <img src={product.logo_url} alt={product.company_name} className="w-10 h-10 object-contain rounded" />
                        ) : (
                          <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                            <Icon className="w-5 h-5 text-blue-600" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-sm text-gray-500">{product.company_name}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        product.category === "credit_card" ? "bg-purple-100 text-purple-700" :
                        product.category === "insurance" ? "bg-green-100 text-green-700" :
                        "bg-blue-100 text-blue-700"
                      }>
                        {getCategoryLabel(product.category)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {product.interest_rate && <p>Rate: {product.interest_rate}</p>}
                        {product.min_amount && product.max_amount && (
                          <p className="text-gray-500">
                            ₹{(product.min_amount / 1000).toFixed(0)}K - ₹{(product.max_amount / 100000).toFixed(0)}L
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-green-100 text-green-700">{product.commission_percent}%</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-medium text-blue-600">{product.click_count || 0}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={product.affiliate_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-gray-100 rounded"
                        >
                          <ExternalLink className="w-4 h-4 text-gray-500" />
                        </a>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(product)}>
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(product.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Banknote className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Financial Products</h3>
            <p className="text-gray-500 mb-4">Add loans, credit cards, and insurance products</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add First Product
            </Button>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
};

export default AdminFinance;
